import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAdminAuth } from "@/hooks/use-admin-auth";
import { isMobileDevice } from "@/lib/device";
import FeatureCard from "@/components/FeatureCard";

export default function Landing() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [showResendDialog, setShowResendDialog] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [inlineMessage, setInlineMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);
  const { toast } = useToast();
  const { isAuthenticated } = useAdminAuth();

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setInlineMessage(null); // Clear any previous messages

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setInlineMessage({ text: "Please enter a valid email address", type: "error" });
      return;
    }

    setLoading(true);
    
    try {
      // First check if email already exists and get its status
      const { data: existingSubscriber } = await supabase
        .from("subscribers")
        .select("status, confirmation_expires_at")
        .eq("email", email)
        .maybeSingle();

      if (existingSubscriber) {
        if (existingSubscriber.status === "confirmed") {
          setInlineMessage({ text: "This email is already subscribed and receiving daily emails. If you're not receiving them, check your spam folder.", type: "error" });
          setLoading(false);
          return;
        } else if (existingSubscriber.status === "pending") {
          // Check if confirmation token is still valid
          const isExpired = existingSubscriber.confirmation_expires_at && 
            new Date(existingSubscriber.confirmation_expires_at) < new Date();
          
          if (isExpired) {
            // Resend confirmation for expired token
            const { error: emailError } = await supabase.functions.invoke('send-confirmation', {
              body: { email }
            });

            if (emailError) {
              throw new Error("Failed to resend confirmation email");
            }

            setSignupSuccess(true);
            setEmail("");
            setLoading(false);
            return;
          } else {
            // Show dialog asking if they want to resend
            setPendingEmail(email);
            setShowResendDialog(true);
            return;
          }
        } else if (existingSubscriber.status === "unsubscribed") {
          // Send confirmation email - edge function will handle reactivating the subscription
          const { error: emailError } = await supabase.functions.invoke('send-confirmation', {
            body: { email }
          });

          if (emailError) {
            console.error('Email error:', emailError);
            throw new Error("Failed to send confirmation email");
          }

          setSignupSuccess(true);
          setEmail("");
          setLoading(false);
          return;
        }
      }

      // If no existing subscriber, create new one
      // Get the active campaign ID
      const { data: activeCampaign, error: campaignError } = await supabase
        .from("campaigns")
        .select("id")
        .eq("is_active", true)
        .single();

      if (campaignError || !activeCampaign) {
        throw new Error("No active campaign found");
      }

      const { error } = await supabase
        .from("subscribers")
        .insert([{ email, campaign_id: activeCampaign.id }]);

      if (error) {
        throw error;
      }

      // Send confirmation email
      const { error: emailError } = await supabase.functions.invoke('send-confirmation', {
        body: { email }
      });

      if (emailError) {
        console.error('Email error:', emailError);
        setInlineMessage({ text: "You're signed up, but there was an issue sending the confirmation email. Please try again later.", type: "error" });
      } else {
        setSignupSuccess(true);
        setEmail("");
      }
    } catch (error: any) {
      // Check if this is a duplicate email error (unique constraint violation)
      const isDuplicateEmail = error.code === "23505" ||
        error.message?.toLowerCase().includes("duplicate") ||
        error.message?.toLowerCase().includes("subscriber_email_key");

      if (isDuplicateEmail) {
        setInlineMessage({
          text: "This email is already subscribed and receiving daily emails. If you're not receiving them, check your spam folder.",
          type: "error"
        });
      } else {
        setInlineMessage({ text: error.message || "Failed to sign up. Please try again.", type: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    setLoading(true);
    setShowResendDialog(false);
    setInlineMessage(null);

    try {
      const { error: emailError } = await supabase.functions.invoke('send-confirmation', {
        body: { email: pendingEmail }
      });

      if (emailError) {
        throw new Error("Failed to resend confirmation email");
      }

      setSignupSuccess(true);
      setEmail("");
      setPendingEmail("");
    } catch (error: any) {
      setInlineMessage({ text: error.message || "Failed to resend confirmation. Please try again.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelResend = () => {
    setShowResendDialog(false);
    setPendingEmail("");
    setEmail("");
  };

  const handleSubscribeAnother = () => {
    setSignupSuccess(false);
    setInlineMessage(null);
    setEmail("");
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="bg-slate-100 shadow-sm hidden md:block">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-2">
            <div
              className="text-white p-2 rounded-xl"
              style={{
                background: 'linear-gradient(150deg, rgba(0, 52, 184, 1) 0%, rgba(43, 97, 235, 1) 41%, rgba(121, 60, 236, 1) 100%)'
              }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <span className="text-sm font-semibold text-gray-900">21-DAY AI CHALLENGE</span>
          </div>
        </div>
      </header>

      {/* Hero Section with Gradient */}
      <div
        className="py-12 md:py-14"
        style={{
          background: 'linear-gradient(150deg, rgba(0, 52, 184, 1) 0%, rgba(43, 97, 235, 1) 41%, rgba(121, 60, 236, 1) 89%)'
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mt-6 mb-2 text-white font-serif">
              21-Day AI Challenge
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto mt-6 md:mt-0 px-6 md:px-0" style={{ lineHeight: '1.1' }}>
            Your onramp to endless possibilities, delivered fresh to your inbox.
            </p>
          </div>
        </div>
      </div>

      {/* Signup Section - White Background */}
      <div className="bg-gray-100 py-12 md:py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-lg mx-auto">
            {/* Main CTA Card */}
            <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-8 md:p-10">
              <h2 className="text-2xl font-semibold mb-3 text-center text-gray-900 font-serif">Start Your Journey</h2>
              {/* <p className="text-center text-gray-600 mb-6">
                21 days. 21 emails. Master AI tools that maximize your time and amplify your impact.
              </p> */}

              {signupSuccess ? (
                <div className="space-y-4 animate-fade-in">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <p className="text-green-800 font-medium">
                      <strong style={{ fontWeight: "600" }}>Success!</strong><br />Check your email to activate your 21-day challenge.
                    </p>
                  </div>
                  <Button
                    onClick={handleSubscribeAnother}
                    variant="outline"
                    className="w-full h-12 font-medium text-base"
                  >
                    Subscribe Another Email
                  </Button>
                  <p className="text-sm text-gray-500 text-center">
                    No spam. Unsubscribe anytime.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSignup} className="space-y-4">
                  <Input
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full h-12 px-4 text-base"
                    disabled={loading}
                    autoFocus={!isMobileDevice()}
                  />
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-[#7C3AEC] hover:bg-[#6B32D1] text-white font-medium text-base flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <span>Signing up</span>
                        <div className="pl-2">
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" title="Loading">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </div>
                      </>
                    ) : (
                      "Get Started"
                    )}
                  </Button>
                  {inlineMessage && (
                    <div className={`rounded-lg p-4 text-center animate-fade-in ${
                      inlineMessage.type === "error"
                        ? "bg-red-50 border border-red-200"
                        : "bg-green-50 border border-green-200"
                    }`}>
                      <p className={inlineMessage.type === "error" ? "text-red-800" : "text-green-800"}>
                        {inlineMessage.text}
                      </p>
                    </div>
                  )}
                  <p className="text-sm text-gray-500 text-center">
                    No spam. Unsubscribe anytime.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Features Section - Gray Background */}
      <div className="py-12 md:py-16 pt-10 flex-1 features-gradient">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Features */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              <FeatureCard
                icon={
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                }
                iconBgColor="bg-teal-100"
                iconColor="text-teal-600"
                title="Master the Basics"
                description="Learn essential AI fundamentals through hands-on challenges that build your confidence."
              />
              <FeatureCard
                icon={
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                  </svg>
                }
                iconBgColor="bg-blue-100"
                iconColor="text-blue-600"
                title="Explore Pro Tools"
                description="Get practical experience with industry-leading AI tools that boost productivity and creativity."
              />
              <FeatureCard
                icon={
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 20h18M5 20l3-7 4 3 5-8 4 5v7" />
                  </svg>
                }
                iconBgColor="bg-purple-100"
                iconColor="text-purple-600"
                title="Expand Your Vision"
                description="Transform your perspective and discover new opportunities powered by AI innovation."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-100 border-t border-gray-200 mt-auto">
        <div className="container mx-auto px-4 md:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4 md:items-end">
            {/* Left: Links */}
            <div className="flex flex-col md:flex-row items-center md:items-end md:justify-start gap-4 md:gap-6 text-sm text-gray-600 order-1 md:order-1">
              <Link to="/privacy" className="hover:text-gray-900 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="hover:text-gray-900 transition-colors">
                Terms & Conditions
              </Link>
            </div>

            {/* Center: Admin Link + Copyright */}
            <div className="flex flex-col items-center justify-center md:justify-end gap-2 order-3 md:order-2">
              {isAuthenticated && (
                <Link
                  to="/admin"
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Admin Dashboard →
                </Link>
              )}
              <span className="text-sm text-gray-700">© {new Date().getFullYear()} Avodah. All rights reserved.</span>
            </div>

            {/* Right: Powered by */}
            <div className="flex items-center justify-center md:justify-end order-2 md:order-3">
              <a
                href="https://www.avodah.dev/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              >
                <span className="text-sm text-gray-500">Powered by</span>
                <img src="/avodah-logo.png" alt="Avodah" className="h-6" />
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Resend Confirmation Dialog */}
      <AlertDialog open={showResendDialog} onOpenChange={setShowResendDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmation Already Sent</AlertDialogTitle>
            <AlertDialogDescription>
              We've already sent a confirmation email to <strong>{pendingEmail}</strong>.
              It may take a few minutes to arrive, so please check your inbox and spam folder.
              <br /><br />
              Would you like us to send another confirmation email?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelResend}>
              I'll Check My Email
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleResendConfirmation} disabled={loading}>
              {loading ? "Sending..." : "Resend Confirmation"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
